import { AudioEngine } from "./audio";
import { displayMetrics } from "./motion";
import { CROP_ORDER, CROPS, STAGE_LABELS } from "./catalog";
import { qualityLabel, harvestYield } from "./farming";
import { Controls } from "./input";
import { distance, lerp } from "./math";
import { readSave, serialize } from "./persistence";
import { activeSaveKey, activeSaveKeys, connectAccountStorage } from './account-storage';
import { Renderer } from "./renderer";
import { CAMP, GATE, REACH, RECIPES, WELL, worldScale } from "./rules";
import { bossDrawSize, frameCloseBoss } from "./battle-framing";
import type { UpgradeId } from "./rules";
import type { CompanionCommand, FloatingText, Particle, Vec2 } from "./types";
import { World } from "./world";
import { ENEMIES } from "./enemies";
import { gameLayout } from "./layout";
import { REGIONS, regionAt } from "./regions";
import { RESOURCES } from "./resources";
import type { FarmAction } from "./world";
import { EXPEDITIONS } from "./expeditions";
import { SceneTap } from "./tap";
import { GEAR, craftGear, equipGear, recycleGear } from "./equipment";

const el = <T extends HTMLElement = HTMLElement>(id: string): T => {
  const node = document.getElementById(id);
  if (!node) throw new Error(`缺少界面节点 ${id}`);
  return node as T;
};
const escapeHtml = (text: string): string =>
  text.replace(
    /[&<>"']/g,
    (char) =>
      ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" })[
        char
      ]!,
  );

/** 数值面板与按钮分开刷新，结构不变时保留按压、焦点和指针捕获。 */
export function patchHtml(node: HTMLElement, html: string): void {
  if (node.dataset.rendered === html) return;
  node.innerHTML = html;
  node.dataset.rendered = html;
}
const button = (
  action: string,
  label: string,
  disabled = false,
  accent = false,
): string =>
  `<button class="action-button ${accent ? "accent" : ""}" data-action="${action}" ${disabled ? "disabled" : ""}>${label}</button>`;

export class Game {
  private readonly sample = new URLSearchParams(location.search).has("sample");
  private readonly taps = new SceneTap();
  private frameCosts: number[] = [];
  world = new World();
  private readonly renderer: Renderer;
  private readonly audio = new AudioEngine();
  private readonly controls: Controls;
  private running = false;
  private paused = false;
  private companionsOpen = false;
  private activity = "bag";
  private lastTime = 0;
  private animationTime = 0;
  private uiTimer = 0;
  private saveTimer = 0;
  private aim: Vec2 | null = null;
  private pointer: Vec2 = { x: 730, y: 450 };
  private camera: Vec2 = { x: 770, y: 455 };
  private particles: Particle[] = [];
  private texts: FloatingText[] = [];
  private namingId: number | null = null;
  private travelRemaining = 0;
  private lastScene = "garden";
  private compactCombat = false;
  private viewHeight = 900;
  private viewWidth = 1600;
  private readonly reducedMotion = window.matchMedia(
    "(prefers-reduced-motion: reduce)",
  ).matches;

  constructor(private readonly canvas: HTMLCanvasElement) {
    this.renderer = new Renderer(canvas);
    this.controls = new Controls(
      () => this.isActive(),
      (command) => this.action(command),
    );
    this.bind();
    this.refresh();
    const saved = this.existingSave();
    el("continue-game").classList.toggle("hidden", !saved);
    if (saved) {
      el("continue-game").classList.replace("text-button", "primary-button");
      el("start-game").classList.replace("primary-button", "text-button");
    }
    requestAnimationFrame((t) => this.frame(t));
    if (!this.sample) {
      connectAccountStorage(() => this.save(false), () => this.accountChanged());
      window.addEventListener('fribench:panel-open', () => this.pause(true));
    }
    if (this.sample) {
      el("landing").classList.add("hidden");
      this.start(this.qualitySample());
      const tools = document.createElement("nav");
      tools.className = "sample-tools";
      tools.innerHTML =
        '<strong>D2 · 原画体型与自然岛岸 · 不写存档</strong><button data-action="sample-garden">庭园</button><button data-action="sample-bridge">接缝</button><button data-action="sample-roster">全员</button><button data-action="sample-battle">战斗</button><button data-action="sample-boss">荆冠</button><button data-action="sample-tortoise">玄甲</button><a href="/atelier.html?muted=1">动效</a>';
      document.getElementById("app")!.append(tools);
    }
  }
  private existingSave(): World | null {
    if (this.sample) return null;
    try {
      return readSave(localStorage, activeSaveKeys());
    } catch {
      return null;
    }
  }
  private accountChanged(): void {
    this.running = false;
    this.paused = false;
    this.controls.clear();
    this.world = new World();
    this.particles = []; this.texts = []; this.travelRemaining = 0; this.companionsOpen = false;
    document.querySelectorAll<HTMLDialogElement>('dialog[open]').forEach(dialog => dialog.close());
    el('landing').classList.remove('hidden'); el('hud').classList.add('hidden'); el('pause-layer').classList.add('hidden');
    el('continue-game').classList.toggle('hidden', !this.existingSave());
    this.refresh();
    this.toast('已切换独立庭园。点击继续游戏载入；游客数据未自动上传。');
  }
  private isActive(): boolean {
    return (
      this.running &&
      !this.paused &&
      !this.companionsOpen &&
      !(this.world.enemies.length > 0 && this.world.selectedPlotId !== null) &&
      this.travelRemaining <= 0 &&
      !document.querySelector("dialog[open]")
    );
  }
  private bind(): void {
    document.addEventListener("input", (e) => {
      const input = e.target as HTMLInputElement;
      const group = input.dataset?.audioGroup as
        "music" | "ambience" | "sfx" | undefined;
      if (group) this.audio.setVolume(group, Number(input.value) / 100);
    });
    window.addEventListener("resize", () => {
      this.controls.clear();
      this.renderer.resize();
    });
    window.addEventListener("pagehide", () => {
      this.save(false);
      this.controls.clear();
    });
    el("start-game").addEventListener("click", () => {
      if (this.existingSave()) this.open("restart-dialog");
      else this.start(new World());
    });
    el("continue-game").addEventListener("click", () =>
      this.start(this.existingSave() ?? new World()),
    );
    el("save-button").addEventListener("click", () => this.save(true));
    el("audio-button").addEventListener("click", () => {
      el("audio-button").textContent = this.audio.toggle() ? "音" : "静";
    });
    el("help-button").addEventListener("click", () => this.open("help-dialog"));
    el("close-farm").addEventListener("click", () => {
      this.world.selectedPlotId = null;
      this.refresh();
    });
    el("resume-button").addEventListener("click", () => this.pause(false));
    el("restart-button").addEventListener("click", () =>
      this.open("restart-dialog"),
    );
    el("cancel-name").addEventListener("click", () =>
      el<HTMLDialogElement>("name-dialog").close(),
    );
    el("confirm-name").addEventListener("click", () => {
      const c = this.world.companions.find((c) => c.id === this.namingId),
        name = el<HTMLInputElement>("name-input").value.trim();
      if (!c || !name) return;
      c.name = Array.from(name).slice(0, 10).join("");
      el<HTMLDialogElement>("name-dialog").close();
      this.refresh();
      this.save(false);
    });
    document.addEventListener("click", (event) => {
      const target =
        event.target instanceof Element
          ? event.target.closest<HTMLButtonElement>("button")
          : null;
      if (!target || target.disabled) return;
      if (target.classList.contains("dialog-close")) {
        target.closest("dialog")?.close();
        return;
      }
      if (target.dataset.action) this.action(target.dataset.action);
    });
    document.querySelectorAll("dialog").forEach((dialog) =>
      dialog.addEventListener("close", () => {
        this.controls.clear();
        this.lastTime = performance.now();
      }),
    );
    this.canvas.addEventListener("pointermove", (e) => {
      this.pointer = this.renderer.screenToWorld(
        e.clientX,
        e.clientY,
        this.camera,
      );
      if (e.pointerType === "mouse") this.aim = this.pointer;
    });
    const pick = (e: PointerEvent) => {
      if (e.button !== 0 || !this.isActive()) return;
      if (!this.renderer.containsScreen(e.clientX, e.clientY)) return;
      e.preventDefault();
      this.audio.unlock();
      const pos = this.renderer.screenToWorld(
        e.clientX,
        e.clientY,
        this.camera,
      );
      this.pointer = pos;
      if (e.pointerType === "touch") this.aim = null;
      const w = this.world;
      const enemy = w.enemies.find(
        (a) =>
          Math.abs(pos.x - a.pos.x) <
            (a.boss
              ? bossDrawSize(this.compactCombat) * 0.44
              : a.kind === "brute"
                ? 68
                : 48) &&
          pos.y >
            a.pos.y -
              (a.boss
                ? (bossDrawSize(this.compactCombat) * 238) / 256
                : a.kind === "brute"
                  ? 145
                  : 100) &&
          pos.y < a.pos.y + 14,
      );
      if (enemy) {
        this.aim = enemy.pos;
        w.attack(enemy.pos);
        if (e.pointerType !== "touch") {
          this.controls.state.attackPointers.add(e.pointerId);
          this.canvas.setPointerCapture(e.pointerId);
        }
        return;
      }
      // 路标和归航门的绘制体积高于落脚点；优先于相邻资源，避免点路标却开始采石。
      if (w.scene === "expedition" && w.run) {
        const markerHit = (point: Vec2): boolean =>
          Math.abs(pos.x - point.x) < 58 &&
          pos.y > point.y - 105 &&
          pos.y < point.y + 22;
        const objective = w.run.objectives.find(
          (o) => !o.complete && markerHit(o.pos),
        );
        if (objective || markerHit(w.run.exit)) {
          w.selectedPlotId = null;
          this.companionsOpen = false;
          w.go({ kind: "objective", id: objective?.id ?? "exit" });
          this.refresh();
          return;
        }
      }
      const plot = w.plots
        .filter(
          (p) =>
            Math.abs(p.pos.x - pos.x) < 51 &&
            pos.y > p.pos.y - 110 &&
            pos.y < p.pos.y + 28,
        )
        .sort(
          (a, b) =>
            distance({ x: a.pos.x, y: a.pos.y - 35 }, pos) -
            distance({ x: b.pos.x, y: b.pos.y - 35 }, pos),
        )[0];
      if (plot && !w.expedition) {
        w.selectedPlotId = plot.id;
        this.companionsOpen = false;
        w.go({ kind: "plot", id: plot.id });
        this.refresh();
        return;
      }
      const companion = w.companions.find(
        (a) =>
          Math.abs(a.pos.x - pos.x) < displayMetrics(a.cropId).width * 0.36 &&
          pos.y > a.pos.y - displayMetrics(a.cropId).height * 0.8 &&
          pos.y < a.pos.y + 12,
      );
      if (companion) {
        w.selectedCompanionId = companion.id;
        this.companionsOpen = true;
        w.selectedPlotId = null;
        this.refresh();
        return;
      }
      const node = w.resourceNodes.find(
        (n) =>
          n.active &&
          Math.abs(pos.x - n.pos.x) < (n.kind === "tree" ? 72 : 48) &&
          pos.y > n.pos.y - (n.kind === "tree" ? 160 : 92) &&
          pos.y < n.pos.y + 14,
      );
      if (node) {
        w.selectedPlotId = null;
        w.go({ kind: "resource", id: node.id });
        this.refresh();
        return;
      }
      if (w.scene === "garden" && distance(pos, WELL) < 75) {
        w.go({ kind: "well" });
        return;
      }
      if (w.scene === "garden" && distance(pos, GATE) < 55) {
        this.action("expedition");
        return;
      }
      if (w.scene === "garden" && distance(pos, CAMP) < 45) {
        this.action("bag");
        return;
      }
      w.selectedPlotId = null;
      this.companionsOpen = false;
      w.go({ kind: "ground", pos });
      this.refresh();
    };
    this.canvas.addEventListener("pointerdown", (e) => {
      if (e.pointerType === "touch") {
        if (this.renderer.containsScreen(e.clientX, e.clientY))
          this.taps.begin(e.pointerId, e.clientX, e.clientY, e.timeStamp);
      } else pick(e);
    });
    this.canvas.addEventListener("pointermove", (e) =>
      this.taps.move(e.pointerId, e.clientX, e.clientY),
    );
    this.canvas.addEventListener("pointerup", (e) => {
      if (
        e.pointerType === "touch" &&
        this.taps.release(e.pointerId, e.clientX, e.clientY, e.timeStamp)
      )
        pick(e);
    });
    this.canvas.addEventListener("pointercancel", () => this.taps.cancel());
    for (const type of ["pointerup", "pointercancel", "lostpointercapture"])
      this.canvas.addEventListener(type, (e) =>
        this.controls.state.release((e as PointerEvent).pointerId),
      );
    this.canvas.addEventListener("contextmenu", (e) => e.preventDefault());
  }
  private start(world: World): void {
    this.world = world;
    this.running = true;
    this.paused = false;
    this.camera = { x: world.player.pos.x, y: world.player.pos.y - 40 };
    el("landing").classList.add("hidden");
    el("hud").classList.remove("hidden");
    el("pause-layer").classList.add("hidden");
    this.controls.clear();
    this.audio.unlock();
    this.refresh();
    this.toast("点选灵田开始照料。手机左侧摇杆移动，右侧按钮互动。");
  }
  private sampleEnemyKind: "mite" | "brute" | "spitter" | "sentinel" | "moth" =
    "mite";
  private qualitySample(): World {
    const w = new World();
    w.inventory.wood = 30;
    w.inventory.stone = 30;
    w.inventory.islandDust = 15;
    w.inventory.spiritDew = 15;
    const p = w.plots[0];
    p.growth = 1;
    p.stage = "mature";
    w.player.pos = { ...p.pos };
    w.requestFarm("awaken", p.id);
    w.player.pos = { x: 730, y: 545 };
    w.selectedPlotId = null;
    w.companions.forEach((c) => {
      c.pos = { x: 650, y: 570 };
      c.command = "follow";
    });
    CROP_ORDER.forEach((cropId, i) => {
      const plot = w.plots[i + 1];
      plot.cropId = cropId;
      plot.growth = 1;
      plot.stage = "mature";
      plot.moisture = 0.8;
    });
    w.signals = [];
    return w;
  }
  private pause(value: boolean): void {
    if (!this.running) return;
    this.paused = value;
    this.controls.clear();
    el("pause-layer").classList.toggle("hidden", !value);
    if (value) this.save(false);
  }
  private open(id: string): void {
    this.controls.clear();
    const d = el<HTMLDialogElement>(id);
    if (!d.open) d.showModal();
  }
  /** 过场只冻结输入和仿真，不用延迟回调修改世界，避免切后台后跨地图误操作。 */
  private transition(title: string, note: string): void {
    this.travelRemaining = this.reducedMotion ? 0.3 : 1.25;
    this.controls.clear();
    this.particles = [];
    this.texts = [];
    this.camera = {
      x: this.world.controlled.pos.x,
      y: this.world.controlled.pos.y - 35,
    };
    el("travel-title").textContent = title;
    el("travel-note").textContent = note;
    el("travel-transition").classList.remove("hidden");
  }
  private action(command: string): void {
    const w = this.world;
    if (command.startsWith("sample-") && this.sample) {
      const sample = this.qualitySample();
      if (command === "sample-bridge") {
        sample.player.pos = { x: 350, y: 510 };
        sample.companions[0].pos = { x: 280, y: 520 };
      }
      if (command === "sample-roster") {
        const template = sample.companions[0];
        sample.companions = CROP_ORDER.map((cropId, i) => {
          const c = CROPS[cropId],
            pos = { x: 555 + (i % 4) * 140, y: i < 4 ? 535 : 665 };
          return {
            ...structuredClone(template),
            id: 800 + i,
            cropId,
            name: c.companionName,
            pos,
            anchor: { ...pos },
            health: c.companionHp,
            maxHealth: c.companionHp,
            damage: c.companionDamage,
            speed: c.companionSpeed * 1.35,
            command: "guard" as const,
            motion: undefined,
            animation: undefined,
            facing: Math.PI / 2,
          };
        });
        sample.player.pos = { x: 950, y: 660 };
        sample.selectedCompanionId = sample.companions[0].id;
        sample.progress.capacity = 2;
      }
      if (command === "sample-battle") {
        sample.player.pos = { x: 730, y: 550 };
        sample.enemies.push({
          id: 990,
          kind: this.sampleEnemyKind,
          pos: { x: 900, y: 550 },
          velocity: { x: 0, y: 0 },
          radius: 21,
          health: 150,
          maxHealth: 150,
          speed: 90,
          damage: 10,
          facing: Math.PI,
          attackCooldown: 1,
          flash: 0,
          wobble: 0,
        });
      }
      if (command === "sample-boss" || command === "sample-tortoise") {
        sample.startExpedition(
          command === "sample-tortoise" ? "ember-quarry" : "verdant-ruins",
        );
        sample.run!.objectives.forEach((o) => (o.complete = true));
        sample.player.pos = { ...sample.run!.objectives.at(-1)!.pos };
        sample.enemies = [];
        sample.run!.encounters.forEach((e) => {
          e.triggered = true;
          e.cleared = true;
          e.enemyIds = [];
        });
        sample.player.pos = { x: 1550, y: 500 };
        sample.tick(0.016, { move: { x: 0, y: 0 }, attack: false, aim: null });
        sample.companions.forEach((c) => (c.pos = { x: 1500, y: 570 }));
      }
      if (command === "sample-battle") {
        this.toast(ENEMIES[this.sampleEnemyKind].name);
        const kinds = ["mite", "brute", "spitter", "sentinel", "moth"] as const;
        this.sampleEnemyKind =
          kinds[(kinds.indexOf(this.sampleEnemyKind) + 1) % kinds.length];
      }
      this.companionsOpen = false;
      this.start(sample);
      return;
    }
    const awakenedBefore = w.progress.awakened;
    if (command === "escape") {
      const dialog = document.querySelector<HTMLDialogElement>("dialog[open]");
      if (dialog) dialog.close();
      else if (this.companionsOpen || w.selectedPlotId !== null) {
        this.companionsOpen = false;
        w.selectedPlotId = null;
        this.refresh();
      } else this.pause(!this.paused);
      return;
    }
    if (command === "pause") {
      this.pause(true);
      return;
    }
    if (command === "confirm-restart") {
      try {
        const raw = activeSaveKeys().map(key => localStorage.getItem(key)).find(Boolean);
        if (raw) localStorage.setItem(activeSaveKey() + ':before-restart', raw);
      } catch {
        this.toast("无法备份当前庭园，重置已取消");
        return;
      }
      document
        .querySelectorAll<HTMLDialogElement>("dialog[open]")
        .forEach((d) => d.close());
      this.start(new World());
      this.save(false);
      return;
    }
    if (!this.running || this.paused || this.travelRemaining > 0) return;
    if (command === "guard") {
      w.guard();
      return;
    }
    if (command.startsWith("craft-gear-")) {
      if (craftGear(w.equipment, w.inventory, command.slice(11))) {
        w.progress.crafted++;
        w.performMotion(w.player, "craft", 1);
        this.audio.play("collect");
      }
      this.renderActivity();
      this.save(false);
      return;
    }
    if (command.startsWith("equip-gear-")) {
      equipGear(w.equipment, command.slice(11));
      this.renderActivity();
      this.save(false);
      return;
    }
    if (command.startsWith("recycle-gear-")) {
      recycleGear(w.equipment, w.inventory, command.slice(13));
      this.renderActivity();
      this.save(false);
      return;
    }
    if (
      command === "bag" ||
      command === "journal" ||
      command === "expedition" ||
      command === "fields" ||
      command === "awakening" ||
      command === "menu" ||
      command === "bestiary" ||
      command === "map"
    ) {
      this.activity = command;
      this.renderActivity();
      this.open("activity-dialog");
      return;
    }
    if (command.startsWith("travel-")) {
      if (w.scene !== "garden") {
        this.toast("庭园导航不可跨越雾海，请先归航");
        return;
      }
      const region = REGIONS.find((r) => r.id === command.slice(7));
      if (region) {
        el<HTMLDialogElement>("activity-dialog").close();
        w.selectedPlotId = null;
        this.companionsOpen = false;
        w.go({ kind: "ground", pos: { x: region.x, y: region.y + 80 } });
      }
    } else if (command === "home") {
      el<HTMLDialogElement>("activity-dialog").close();
      w.retreat();
      w.selectedPlotId = null;
      this.companionsOpen = false;
    } else if (command.startsWith("seed-"))
      w.selectedSeed = CROP_ORDER[Number(command.slice(5))] ?? "pumpkin";
    else if (command.startsWith("plot-")) {
      if (w.expedition) {
        this.toast("先撤离雾境，再照料灵田");
        return;
      }
      el<HTMLDialogElement>("activity-dialog").close();
      this.companionsOpen = false;
      w.go({ kind: "plot", id: Number(command.slice(5)) });
    } else if (command.startsWith("farm-"))
      w.requestFarm(command.slice(5) as FarmAction);
    else if (command.startsWith("buy-")) {
      const id = CROP_ORDER[Number(command.slice(4))];
      if (id) w.buySeed(id);
    } else if (command.startsWith("upgrade-"))
      w.upgrade(command.slice(8) as UpgradeId);
    else if (command.startsWith("select-"))
      w.selectedCompanionId = Number(command.slice(7));
    else if (command.startsWith("command-")) {
      w.command(
        command.slice(8) as CompanionCommand,
        document.querySelector<HTMLInputElement>("#all-companions")?.checked ??
          false,
      );
      this.companionsOpen = false;
    } else if (command === "companions") {
      this.companionsOpen = !this.companionsOpen;
      w.selectedPlotId = null;
    } else if (command === "close-companions") this.companionsOpen = false;
    else if (command === "next-companion") {
      w.nextCompanion();
      this.companionsOpen = true;
      w.selectedPlotId = null;
    } else if (command === "well") {
      if (w.scene !== "garden") {
        this.toast("灵泉位于庭园，远征可采药草补水");
        return;
      }
      el<HTMLDialogElement>("activity-dialog").close();
      w.go({ kind: "well" });
      w.selectedPlotId = null;
    } else if (command === "save") {
      this.save(true);
    } else if (command === "audio") {
      el("audio-button").textContent = this.audio.toggle() ? "音" : "静";
    } else if (command === "compost") w.compost();
    else if (command === "distill") w.distill();
    else if (command === "rescue") w.starterSeeds();
    else if (command === "deliver") w.deliver();
    else if (command === "claim") w.claimQuest();
    else if (command === "eat") w.eat();
    else if (command === "ward") w.ward();
    else if (command === "dash") w.dash();
    else if (command === "skill") w.skill();
    else if (command === "attack-mode") {
      w.cycleAttackMode();
      this.toast(
        w.attackMode === "seed"
          ? "星芒法杖 · 远程三段连射"
          : "月刃法杖 · 近身横扫与回旋",
      );
    } else if (command === "possess") {
      w.possess();
      this.companionsOpen = false;
      w.selectedPlotId = null;
    } else if (command === "interact") w.interact();
    else if (command.startsWith("begin-expedition")) {
      el<HTMLDialogElement>("activity-dialog").close();
      w.startExpedition(
        command.endsWith("ember-quarry") ? "ember-quarry" : "verdant-ruins",
      );
      w.selectedPlotId = null;
      this.companionsOpen = false;
    } else if (command === "extract") {
      el<HTMLDialogElement>("activity-dialog").close();
      w.extractExpedition();
    } else if (command.startsWith("objective-")) {
      el<HTMLDialogElement>("activity-dialog").close();
      w.go({ kind: "objective", id: command.slice(10) });
    } else if (command === "retreat") {
      el<HTMLDialogElement>("activity-dialog").close();
      w.retreat();
    } else if (command === "name" && w.selectedCompanion) {
      this.namingId = w.selectedCompanion.id;
      el<HTMLInputElement>("name-input").value = w.selectedCompanion.name;
      this.open("name-dialog");
    }
    if (w.progress.awakened > awakenedBefore) {
      w.selectedPlotId = null;
      this.companionsOpen = true;
    }
    this.flushSignals();
    this.refresh();
    if (el<HTMLDialogElement>("activity-dialog").open) this.renderActivity();
  }
  private frame(timestamp: number): void {
    const began = performance.now();
    const dt = Math.min(
      0.05,
      Math.max(0, (timestamp - this.lastTime) / 1000 || 0),
    );
    this.lastTime = timestamp;
    this.animationTime += dt;
    if (this.isActive())
      this.audio.update(
        dt,
        this.world.boss
          ? "boss"
          : this.world.enemies.length
            ? "battle"
            : this.world.scene === "expedition"
              ? "explore"
              : regionAt(this.world.player.pos).id,
      );
    if (this.world.scene !== this.lastScene) {
      this.lastScene = this.world.scene;
      this.transition(
        this.world.mapDefinition?.name ?? "青屿庭园",
        this.world.scene === "garden"
          ? "把冒险的收获，化作下一次生长。"
          : "探索遗迹 · 收集资源 · 挑战守护者",
      );
    }
    if (this.travelRemaining > 0) {
      this.travelRemaining = Math.max(0, this.travelRemaining - dt);
      if (this.travelRemaining === 0)
        el("travel-transition").classList.add("hidden");
    }
    if (this.isActive()) {
      const awakenedBefore = this.world.progress.awakened;
      if (
        Math.hypot(this.controls.state.move.x, this.controls.state.move.y) >
          0.05 ||
        this.controls.state.attacking
      ) {
        this.world.selectedPlotId = null;
        this.companionsOpen = false;
      }
      this.world.tick(dt, {
        move: this.controls.state.move,
        attack: this.controls.state.attacking,
        aim: this.controls.touch ? null : this.aim,
      });
      if (this.world.progress.awakened > awakenedBefore) {
        this.world.selectedPlotId = null;
        this.companionsOpen = true;
      }
      this.flushSignals();
      this.saveTimer += dt;
      if (this.saveTimer > 10) {
        this.saveTimer = 0;
        this.save(false);
      }
      const p = this.world.controlled.pos;
      this.camera.x = lerp(this.camera.x, p.x, 1 - Math.exp(-dt * 5));
      this.camera.y = lerp(this.camera.y, p.y - 35, 1 - Math.exp(-dt * 5));
    }
    for (const p of this.particles) {
      p.life -= dt;
      p.pos.x += p.velocity.x * dt;
      p.pos.y += p.velocity.y * dt;
      p.velocity.y += p.gravity * dt;
    }
    this.particles = this.particles.filter((p) => p.life > 0);
    for (const t of this.texts) {
      t.life -= dt;
      t.pos.y -= dt * 25;
    }
    this.texts = this.texts.filter((t) => t.life > 0);
    this.uiTimer += dt;
    if (this.uiTimer > 0.12) {
      this.uiTimer = 0;
      this.refresh();
    }
    this.fitBattleCamera();
    this.renderer.draw({
      compactCombat: this.compactCombat,
      time: this.animationTime,
      scene: this.world.scene,
      run: this.world.run,
      attackMode: this.world.attackMode,
      dayTime: this.world.dayTime,
      weather: this.world.weather,
      camera: this.camera,
      cameraShake: this.reducedMotion
        ? 0
        : this.world.effects.some((e) => e.kind === "impact" && e.life > 0.19)
          ? 2.5
          : 0,
      effects: this.world.effects,
      ghosts: this.world.ghosts,
      pointerWorld: this.pointer,
      player: this.world.player,
      plots: this.world.plots,
      companions: this.world.companions,
      enemies: this.world.enemies,
      resourceNodes: this.world.resourceNodes,
      particles: this.particles,
      floatingTexts: this.texts,
      selectedPlotId: this.world.selectedPlotId,
      selectedCompanionId: this.world.selectedCompanionId,
      possessedCompanionId: this.world.possessedCompanionId,
      threats: this.world.threats,
      bolts: this.world.bolts,
      destination: this.world.targetPosition(),
    });
    if (this.sample) {
      this.frameCosts.push(performance.now() - began);
      if (this.frameCosts.length > 120) this.frameCosts.shift();
      const sorted = [...this.frameCosts].sort((a, b) => a - b);
      el("game-canvas").dataset.performance =
        `${this.frameCosts.length} frames; render+simulation p95=${(sorted[Math.floor(sorted.length * 0.95)] ?? 0).toFixed(2)}ms; canvas=${this.canvas.width}x${this.canvas.height}; audio=${this.audio.hardMuted ? "hard-muted" : "user setting"}`;
      el("game-canvas").dataset.ai = this.world.companions
        .map((c) => `${c.name}:${c.command}/${c.behavior}`)
        .join(";");
    }
    requestAnimationFrame((t) => this.frame(t));
  }
  private fitBattleCamera(): void {
    this.camera.y = frameCloseBoss(
      this.camera.y,
      this.world.controlled.pos,
      this.world.boss?.pos,
      this.viewHeight,
      worldScale(this.viewWidth, this.viewHeight),
      this.compactCombat,
    );
  }

  private refresh(): void {
    const w = this.world;
    const mode = w.selectedPlot
      ? "farm"
      : this.companionsOpen
        ? "companions"
        : "play";
    const appStyle = getComputedStyle(el("app"));
    const layout = gameLayout(
      window.innerWidth,
      window.innerHeight - (this.sample ? 52 : 0),
      mode,
      this.controls.touch,
      parseFloat(appStyle.paddingTop) || 0,
      parseFloat(appStyle.paddingBottom) || 0,
      w.scene === "expedition",
      parseFloat(appStyle.paddingLeft) || 0,
      parseFloat(appStyle.paddingRight) || 0,
    );
    const hud = el("hud");
    if (this.sample) {
      layout.bottom += 52;
      document.documentElement.classList.add("sample-mode");
    }
    el("audio-button").textContent = this.audio.enabled ? "音" : "静";
    this.compactCombat = layout.combatWings;
    this.viewHeight = layout.viewport.height;
    this.viewWidth = layout.viewport.width;
    this.fitBattleCamera();
    hud.dataset.mode = mode;
    hud.classList.toggle("compact", layout.compact);
    hud.classList.toggle("portrait", layout.portrait);
    hud.classList.toggle("in-battle", w.expedition);
    for (const [key, value] of Object.entries(layout.panel))
      hud.style.setProperty(`--panel-${key}`, `${value}px`);
    hud.style.setProperty("--world-bottom", `${layout.bottom}px`);
    hud.style.setProperty("--world-top", `${layout.top}px`);
    const toastStack = el("toast-stack");
    toastStack.style.left = `${layout.viewport.x + layout.viewport.width / 2}px`;
    toastStack.style.bottom = `${layout.bottom + 12}px`;
    toastStack.style.width = `${Math.min(340, layout.viewport.width - 24)}px`;
    this.renderer.setViewport(layout.viewport);
    el("day-label").textContent =
      `${{ clear: "晴", drizzle: "微雨", rain: "灵雨" }[w.weather]} · 第 ${w.day} 日`;
    el("location-name").textContent =
      w.mapDefinition?.name ?? regionAt(w.controlled.pos).name;
    const readyPlots = w.plots.filter((p) => p.stage === "mature").length;
    patchHtml(
      el("journey-ribbon"),
      w.run
        ? `<span class="ribbon-mark">◇</span><span><strong>${escapeHtml(w.mapDefinition?.name ?? "雾海远征")}</strong> · ${escapeHtml(w.objectiveText)}</span>${button("expedition", "行程")}`
        : `<span class="ribbon-mark">✧</span><span>${readyPlots ? `<strong>${readyPlots} 块灵田已成熟</strong> · 可收获或唤醒为伙伴` : "暂无成熟作物，可选中田地查看生长进度"}</span>${button(readyPlots ? "awakening" : "journal", readyPlots ? "去照料" : "旅程")}`,
    );
    patchHtml(
      el("resource-strip"),
      [
        ["水", w.inventory.water],
        ["肥", w.inventory.fertilizer],
        ["食", w.inventory.food],
        ["灵露", w.inventory.spiritDew],
        ["铜叶", w.progress.coins],
      ]
        .map(
          ([label, value]) =>
            `<div class="resource-pill"><span>${label}</span><strong>${value}</strong></div>`,
        )
        .join(""),
    );
    const q = w.questInfo();
    el("journal-button").textContent = q.ready ? "更多 ✦" : "更多";
    patchHtml(
      el("quest-card"),
      `<p class="eyebrow">青屿旅程 ${Math.min(6, w.progress.quest + 1)}/6</p><h3>${q.title}</h3><p>${q.detail}</p><div class="quest-progress"><span style="width:${Math.min(100, (q.current / q.target) * 100)}%"></span></div>${q.ready ? button("claim", "领取旅程奖励", false, true) : ""}`,
    );
    patchHtml(
      el("event-card"),
      w.expedition
        ? `<strong>${escapeHtml(w.mapDefinition?.name ?? "雾海远征")}</strong><p>${escapeHtml(w.objectiveText)}</p>${button("expedition", "远征行程")}`
        : `<strong>庭园安宁 · 自由经营</strong><p>准备好后，可以带伙伴进入雾境。</p>`,
    );
    const a = w.controlled;
    el("player-status-name").textContent = "name" in a ? a.name : "庭园行者";
    el("player-health").style.width = `${(a.health / a.maxHealth) * 100}%`;
    el("player-health-text").textContent =
      `${Math.ceil(a.health)} / ${a.maxHealth}`;
    el("player-status").setAttribute(
      "aria-label",
      `生命 ${Math.ceil(a.health)} / ${a.maxHealth}`,
    );
    patchHtml(
      el("seed-bar"),
      CROP_ORDER.map((id, i) => {
        const c = CROPS[id];
        return `<button class="seed-card ${w.selectedSeed === id ? "active" : ""}" data-action="seed-${i}" aria-pressed="${w.selectedSeed === id}"><span class="seed-card__icon" style="background:${c.primary}">${c.icon}</span><span><strong>${c.name}</strong><small>种子 ×${w.seeds[id]}</small></span><kbd>${i + 1}</kbd></button>`;
      }).join(""),
    );
    this.renderFarm();
    this.renderCompanions();
    el("boss-meter").classList.toggle("hidden", !w.expedition);
    const boss = w.boss;
    el("wave-label").textContent = boss
      ? `${w.mapDefinition?.bossName} · ${Math.ceil(boss.health)}/${boss.maxHealth}`
      : w.run?.phase === "cleared"
        ? "守护者已平息 · 带上战利品归航"
        : `遗迹守卫 · 剩余 ${w.enemies.length}`;
    el("boss-phase").textContent = boss?.boss
      ? `第 ${boss.boss.phase} 阶段`
      : "探索进度";
    const bossThreat = w.threats.find((threat) => threat.id === boss?.id);
    el("boss-counter").textContent = [
      bossThreat?.label,
      boss?.boss?.weakness ?? w.objectiveText,
    ]
      .filter(Boolean)
      .join(" · ");
    el("wave-progress").style.width =
      `${boss ? (boss.health / boss.maxHealth) * 100 : w.run ? (w.run.objectives.filter((o) => o.complete).length / Math.max(1, w.run.objectives.length)) * 100 : 0}%`;
    el("possession-banner").classList.toggle(
      "hidden",
      w.possessedCompanionId === null,
    );
    el("possession-time").textContent = w.possessionRemaining.toFixed(1);
    el("dash-button").textContent =
      w.dashCooldown > 0 ? `${w.dashCooldown.toFixed(1)}s` : "闪避";
    el("possess-button").textContent =
      w.possessedCompanionId !== null
        ? "回归"
        : w.possessionCooldown > 0
          ? `灵识 ${Math.ceil(w.possessionCooldown)}s`
          : "附身";
    const skillCooldown =
      w.possessedCompanionId === null ? w.bloomCooldown : w.skillCooldown;
    el("skill-button").textContent =
      skillCooldown > 0
        ? `${Math.ceil(skillCooldown)}s`
        : w.possessedCompanionId === null
          ? "灵潮"
          : "灵技";
    el<HTMLButtonElement>("skill-button").disabled = skillCooldown > 0;
    el("attack-mode-button").textContent =
      w.attackMode === "seed" ? "星芒" : "月刃";
    el<HTMLButtonElement>("attack-mode-button").disabled =
      w.possessedCompanionId !== null;
    el("attack-mode-button").setAttribute(
      "aria-label",
      `当前${w.attackMode === "seed" ? "星芒连射" : "月刃横扫"}，点击切换，快捷键 Z`,
    );
    el("attack-mode-button").dataset.mode = w.attackMode;
    for (const [id, remaining, total] of [
      ["skill-button", skillCooldown, w.possessedCompanionId === null ? 6 : 4],
      ["dash-button", w.dashCooldown, 1.2],
      ["possess-button", w.possessionCooldown, 8],
    ] as const) {
      const node = el(id);
      node.style.setProperty(
        "--cooldown",
        `${Math.max(0, Math.min(1, remaining / total)) * 100}%`,
      );
      node.classList.toggle("cooling", remaining > 0);
    }
    el<HTMLButtonElement>("possess-button").disabled =
      !w.selectedCompanion ||
      (w.possessedCompanionId === null && w.possessionCooldown > 0);
    const d = w.destination;
    el("action-status").textContent = d
      ? `${d.kind === "resource" ? "自动采集" : d.kind === "well" ? "前往泉边取水" : d.kind === "plot" && d.action ? "走近灵田后操作" : "正在前往目标"} · 移动可取消`
      : "";
  }
  private renderFarm(): void {
    const w = this.world,
      p = w.selectedPlot;
    el("farm-panel").classList.toggle("hidden", !p);
    if (!p) return;
    el("seed-bar").classList.toggle("hidden", !!p.cropId);
    el("farm-stats").classList.toggle("hidden", !p.cropId);
    el("growth-stages").classList.toggle("hidden", !p.cropId);
    const stages = Object.entries(STAGE_LABELS);
    const currentStage = stages.findIndex(([key]) => key === p.stage);
    patchHtml(
      el("growth-stages"),
      stages
        .map(
          ([key, label], i) =>
            `<span class="${i <= currentStage ? "reached" : ""} ${key === p.stage ? "current" : ""}"><i></i>${label}</span>`,
        )
        .join(""),
    );
    const c = CROPS[p.cropId ?? w.selectedSeed];
    el("farm-title").textContent = p.cropId
      ? `${c.name} · ${STAGE_LABELS[p.stage]}`
      : `第 ${p.id + 1} 块灵田`;
    el("farm-description").textContent = p.cropId
      ? `${c.epithet} · ${p.sprinkler ? "滴灌中 · " : ""}${p.rotationBonus ? "轮作 +12% · " : ""}${w.neighborBonus(p) ? "邻作 +10% · " : ""}${p.moisture < 0.12 ? "缺水停长，先浇水" : p.stage === "mature" ? "选择收获或唤灵" : `约 ${Math.ceil(((1 - p.growth) * c.growSeconds) / (0.6 + w.progress.soil * 0.15))} 秒成熟（随照料变化）`}`
      : `${c.description} 点击播种后自动走近。`;
    patchHtml(
      el("farm-stats"),
      [
        ["成长", p.growth, c.primary],
        ["水分", p.moisture, "#83cfc7"],
        ["肥力", p.fertility, "#d8c177"],
        ["品质", p.quality, c.secondary],
      ]
        .map(
          ([label, val, color]) =>
            `<div class="stat-row"><label>${label}</label><div class="stat-track"><span style="width:${Number(val) * 100}%;background:${color}"></span></div><em>${label === "品质" ? qualityLabel(Number(val)) : Math.round(Number(val) * 100) + "%"}</em></div>`,
        )
        .join(""),
    );
    const mature = p.stage === "mature",
      yieldFood = harvestYield(p).food,
      possessed = w.possessedCompanionId !== null;
    let actions = !p.cropId
      ? button(
          "farm-plant",
          `播种 ${c.name}<small>种子 ×1 · 剩 ${w.seeds[w.selectedSeed]}</small>`,
          w.seeds[w.selectedSeed] < 1 || possessed,
          true,
        )
      : mature
        ? ""
        : button(
            "farm-water",
            `浇水<small>水 ×1 · ${w.inventory.water < 1 ? "先取水" : "补水 48%"}</small>`,
            w.inventory.water < 1 || p.moisture >= 0.96 || possessed,
          ) +
          button(
            "farm-fertilize",
            "施肥<small>肥料 ×1 · 肥力 +44%</small>",
            w.inventory.fertilizer < 1 || p.fertility >= 0.96 || possessed,
          );
    if (mature)
      actions += button(
        "farm-harvest",
        `收获<small>食物 +${yieldFood} · 种子 · 铜叶</small>`,
        possessed,
        true,
      );
    const reason = !p.cropId
      ? "先播种并培育成熟"
      : !mature
        ? `成长 ${Math.round(p.growth * 100)}% · 成熟后解锁`
        : possessed
          ? "先返回本体"
          : w.companions.length >= w.companionLimit
            ? "灵契已满 · 去工坊扩容"
            : w.inventory.spiritDew < c.awakenCost
              ? `灵露不足 · 需要 ${c.awakenCost}`
              : `灵露 ×${c.awakenCost} · ${c.companionName} · 留种 +1`;
    actions += button(
      "farm-awaken",
      `✦ 唤醒灵植<small>${reason}</small>`,
      !p.cropId ||
        !mature ||
        w.inventory.spiritDew < c.awakenCost ||
        w.companions.length >= w.companionLimit ||
        possessed,
      true,
    );
    if (w.inventory.water < 1 && !mature)
      actions += button("well", "取水 · 免费");
    if (possessed) actions += button("possess", "回到本体再耕作");
    patchHtml(el("farm-actions"), actions);
  }
  private renderCompanions(): void {
    const w = this.world,
      c = w.selectedCompanion;
    el("companion-panel").classList.toggle("hidden", !this.companionsOpen);
    el("companion-name").textContent = c?.name ?? "还没有灵植伙伴";
    el("companion-kind").textContent = c
      ? `${CROPS[c.cropId].companionRole}${c.reviveWard ? " · 已护命" : ""}`
      : "成熟作物可以消耗灵露唤醒";
    el("companion-portrait").textContent = c ? CROPS[c.cropId].icon : "芽";
    el("companion-portrait").style.background = c
      ? CROPS[c.cropId].primary
      : "#bed586";
    el("companion-health").style.width = c
      ? `${(c.health / c.maxHealth) * 100}%`
      : "0%";
    const commands: Array<[CompanionCommand, string]> = [
      ["follow", "跟随"],
      ["gather", "采集"],
      ["guard", "守卫"],
      ["focus", "集火"],
      ["retreat", "撤退"],
    ];
    const previous =
      document.querySelector<HTMLInputElement>("#all-companions")?.checked ??
      false;
    patchHtml(
      el("command-buttons"),
      c
        ? `<label class="all-command"><input type="checkbox" id="all-companions" /> 命令全队</label>${commands.map(([id, label]) => `<button class="command-button ${c.command === id ? "active" : ""}" data-action="command-${id}">${label}</button>`).join("")}${button("name", "赐名")}${button("ward", c.reviveWard ? "续生印已绑定" : "续生印 · 2 灵露", c.reviveWard || w.inventory.spiritDew < 2)}`
        : "<p>播种、浇水，成熟后选择「唤灵成伴」。</p>",
    );
    const checkbox =
      document.querySelector<HTMLInputElement>("#all-companions");
    if (checkbox) checkbox.checked = previous;
    patchHtml(
      el("companion-roster"),
      w.companions
        .map(
          (c) =>
            `<button data-action="select-${c.id}" class="${c.id === w.selectedCompanionId ? "active" : ""}">${escapeHtml(c.name)}</button>`,
        )
        .join(""),
    );
  }
  private renderActivity(): void {
    const w = this.world,
      b = w.inventory;
    el("activity-dialog").dataset.page = this.activity;
    for (const tab of document.querySelectorAll<HTMLButtonElement>(
      "#activity-tabs button",
    )) {
      const selected =
        tab.dataset.action === this.activity ||
        (this.activity === "awakening" && tab.dataset.action === "fields");
      tab.classList.toggle("active", selected);
      tab.setAttribute("aria-current", selected ? "page" : "false");
    }
    el("activity-title").textContent =
      this.activity === "map"
        ? "群岛行旅"
        : this.activity === "menu"
          ? "庭园事务"
          : this.activity === "bestiary"
            ? "雾境生灵录"
            : this.activity === "awakening"
              ? "唤醒你的灵植"
              : this.activity === "fields"
                ? "灵田手札"
                : this.activity === "journal"
                  ? "青屿旅程"
                  : this.activity === "expedition"
                    ? "雾海远征"
                    : "行囊与工坊";
    let html = "";
    if (this.activity === "menu") {
      html = `<p>庭园无需抵御定时敌袭，按自己的节奏培育与冒险。</p><div class="shop-grid">${button("map", "群岛行旅 · 地图与导航")}${button("home", "回到庭园 · 召回全队")}${button("well", "灵泉取水 · 免费")}${button("bag", "行囊与工坊")}${button("journal", "旅程与委托")}${button("expedition", "进入雾境")}${button("bestiary", "敌人图鉴与反制")}${button("awakening", "✦ 唤醒灵植")}</div><p>法杖连续第三击穿透。灵潮（Q / 灵技）击退并打断敌人。附身后切换为伙伴专属技能。</p>`;
    } else if (this.activity === "map" && w.run) {
      html = `<div class="expedition-intro"><p class="eyebrow">EXPLORATION LOG</p><h3>${escapeHtml(w.mapDefinition?.name ?? "雾海")}</h3><p>${escapeHtml(w.objectiveText)}</p></div><div class="shop-grid">${w.run.objectives.map((o, i) => button(`objective-${o.id}`, `0${i + 1} · ${escapeHtml(o.name)}<small>${o.complete ? "已完成" : "导航前往 · 抵达后互动"}</small>`, o.complete)).join("")}</div>${button("expedition", "查看远征档案")}`;
    } else if (this.activity === "map") {
      html = `<p>当前位置：${regionAt(w.controlled.pos).name}。三片区域步行连通；点导航自动前往，手动移动取消。</p><div class="region-map" aria-label="从西到东：丰穰林地、青屿庭园、琥珀矿原">${[REGIONS[1], REGIONS[0], REGIONS[2]].map((r) => `<article style="--biome:${r.color}"><h3>${r.name}</h3><p>${r.note}</p>${button(`travel-${r.id}`, "导航前往")}</article>`).join("")}</div><h3>野外采集</h3>${Object.values(
        RESOURCES,
      )
        .map(
          (r) =>
            `<p><strong>${r.name}</strong> · ${r.note}<small> · 采完 ${r.respawn} 秒再生</small></p>`,
        )
        .join(
          "",
        )}<p>野莓供给食物，药草补充水肥，琥珀让纯经营也能稳定获得唤灵材料。雾境仍需主动开启，不会突然袭击田地。</p>${button("home", "召回全队，返回庭园", false, true)}`;
    } else if (this.activity === "bestiary") {
      html =
        Object.entries(ENEMIES)
          .map(
            ([kind, spec]) =>
              `<article class="enemy-card"><span class="atlas-portrait" style="background-image:url('/art/animation-v5/${kind}.webp')"></span><div><h3>${spec.name}</h3><p>${spec.role}</p><p>${spec.counter}</p><small>蓄力 ${spec.windup}s · 破绽 ${spec.recovery}s</small></div></article>`,
          )
          .join("") +
        "<p>第三场起裂岩重卫成为精英，震地范围更大。优先处理远程孢母，保留灵潮用于打断。</p>";
    } else if (this.activity === "fields" || this.activity === "awakening") {
      html =
        (w.scene === "expedition"
          ? '<p class="preparation-note">灵田留在安全的庭园。先归航，再收获、浇水或唤醒伙伴。</p>'
          : "") +
        (this.activity === "awakening"
          ? `<p>种植 → 浇水照料 → 成熟 → 点击金色「唤醒灵植」。不要先收获。当前灵露 ${w.inventory.spiritDew}，伙伴 ${w.companions.length}/${w.companionLimit}。</p>`
          : "<p>点一块灵田即可自动前往。成熟、缺水与滴灌状态在这里一目了然。</p>") +
        '<div class="shop-grid field-grid">' +
        w.plots
          .map((p) =>
            button(
              `plot-${p.id}`,
              `<span class="field-number">${String(p.id + 1).padStart(2, "0")}</span><strong>${p.cropId ? CROPS[p.cropId].name : "待播种的灵田"}</strong><small>${p.stage === "mature" ? `✦ 可唤醒 · 灵露 ${CROPS[p.cropId!].awakenCost}` : p.cropId ? `${STAGE_LABELS[p.stage]} ${Math.round(p.growth * 100)}% · 水分 ${Math.round(p.moisture * 100)}%` : "选择种子后在此播种"}${p.sprinkler ? " · 滴灌" : ""}${p.cropId && p.moisture < 0.2 ? " · 缺水!" : ""}</small><span class="field-progress"><i style="width:${p.growth * 100}%"></i></span>`,
              w.scene !== "garden",
              p.stage === "mature",
            ),
          )
          .join("") +
        "</div><p>不同作物相邻：生长 +10%。上一轮与本轮不同：轮作 +12%。</p>";
    } else if (this.activity === "bag") {
      html = `<p class="inventory-summary">水 ${b.water}/12 · 肥料 ${b.fertilizer} · 食物 ${b.food} · 木材 ${b.wood} · 石料 ${b.stone} · 灵露 ${b.spiritDew} · 浮土 ${b.islandDust} · 铜叶 ${w.progress.coins}</p><h3>补给与换种</h3><div class="shop-grid">${button("well", "泉水补给<small>免费装满水壶 · 自动前往</small>")}${button("compost", "木材堆肥<small>2 木材 → 3 肥料</small>", b.wood < 2)}${button("distill", "酿造灵露<small>4 食物 → 1 灵露</small>", b.food < 4)}${button("eat", "食用<small>优先易腐食物，否则恢复 32 点</small>", w.controlled.health >= w.controlled.maxHealth)}${CROP_ORDER.map((id, i) => button(`buy-${i}`, `${CROPS[id].name}<small>${id === "moonherb" ? 4 : 2} 铜叶 → 2 种子</small>`, w.progress.coins < (id === "moonherb" ? 4 : 2))).join("")}${button("rescue", "庭园援助<small>无种无作物时免费补种</small>", Object.values(w.seeds).some((n) => n > 0) || w.plots.some((p) => p.cropId))}</div><h3>工坊 · 木石改变庭园</h3><div class="shop-grid">`;
      for (const [id, r] of Object.entries(RECIPES) as Array<
        [UpgradeId, typeof RECIPES.tools]
      >) {
        const level =
          id === "sprinkler"
            ? w.selectedPlot?.sprinkler
              ? 1
              : 0
            : w.progress[id];
        const names: Record<string, string> = {
          wood: "木材",
          stone: "石料",
          fertilizer: "肥料",
          islandDust: "浮土",
        };
        const cost = Object.entries(r.cost)
          .map(([key, n]) => `${n * (level + 1)} ${names[key]}`)
          .join(" + ");
        html += `<article><strong>${r.name} · ${level}/${r.max}</strong><p>${r.note}</p>${button(`upgrade-${id}`, level >= r.max ? "已完成" : id === "sprinkler" && !w.selectedPlot ? "先选一块灵田" : cost, level >= r.max || (id === "sprinkler" && !w.selectedPlot))}</article>`;
      }
      html += `</div><h3>行装 · 横向成长</h3><p>每槽一件；更重的装备防御更高、行动更慢。回收返还一半普通材料，灵露与浮土不返还。</p><div class="equipment-grid">${Object.entries(
        GEAR,
      )
        .map(
          ([id, g]) =>
            `<article><strong>${g.name} · ${w.equipment.slots[g.slot] === id ? "已装备" : { weapon: "武器", armor: "防具", charm: "护符" }[g.slot]}</strong><p>${g.note}</p><small>${Object.entries(
              g.cost,
            )
              .map(
                ([k, v]) =>
                  `${({ wood: "木", stone: "石", fertilizer: "肥", spiritDew: "露", islandDust: "土" } as Record<string, string>)[k]} ${v}`,
              )
              .join(" / ")}</small>${
              w.equipment.owned.includes(id)
                ? button(
                    `equip-gear-${id}`,
                    "装备",
                    w.equipment.slots[g.slot] === id,
                  ) + button(`recycle-gear-${id}`, "回收")
                : button(
                    `craft-gear-${id}`,
                    "制作并装备",
                    Object.entries(g.cost).some(
                      ([k, v]) =>
                        w.inventory[k as keyof typeof w.inventory] < v,
                    ),
                  )
            }</article>`,
        )
        .join(
          "",
        )}</div><h3>音景与庭园记忆</h3><div class="shop-grid">${button("save", "立即保存")}${button("audio", this.audio.hardMuted ? "验证已锁定静音" : this.audio.enabled ? "关闭声音" : "开启声音")}</div><div class="audio-settings">${(["music", "ambience", "sfx"] as const).map((group) => `<label>${{ music: "音乐", ambience: "环境", sfx: "音效" }[group]}<input type="range" min="0" max="100" value="${this.audio.settings[group] * 100}" data-audio-group="${group}" aria-label="${group}音量"></label>`).join("")}</div>`;
      if (w.spoilingFood.length)
        html += `<p class="spoil-note">易腐食物 ${w.spoilingFood.reduce((sum, f) => sum + f.amount, 0)} 份 · 游戏内 ${Math.ceil(Math.min(...w.spoilingFood.map((f) => f.remaining)))} 秒后腐坏，食用恢复 14 点。</p>`;
    } else if (this.activity === "journal") {
      const q = w.questInfo(),
        crop = w.orderCrop();
      html = `<article class="journal-card"><h3>${q.title}</h3><p>${q.detail}</p><p>${Math.min(q.current, q.target)} / ${q.target}</p>${button("claim", "领取：8 铜叶 + 1 灵露 + 2 肥料", !q.ready, true)}</article><article class="journal-card"><h3>商旅委托 ${w.progress.order + 1} · ${CROPS[crop].name}</h3><p>本轮收获一次${CROPS[crop].name}，交付 3 份食物。奖励：12 铜叶、2 灵露、2 肥料。</p><p>${w.progress.harvests[crop] > w.progress.orderBaseline ? "✓ 已收获" : "○ 等待收获"} · 食物 ${b.food}/3</p>${button("deliver", "交付委托", !w.orderReady(), true)}</article><p>收获 ${w.totalHarvests} · 唤灵 ${w.progress.awakened} · 建设 ${w.progress.crafted} · 胜利 ${w.progress.victories}</p>`;
    } else {
      const result = w.battleResult
        ? `<p class="result-message">${escapeHtml(w.battleResult)}</p>`
        : "";
      html = `<div class="expedition-intro"><p class="eyebrow">EXPEDITIONS</p><h3>选择远征地图</h3><p>进入独立的探险岛屿，采集、解开遗迹，再挑战守护者。庭园不会刷怪，不设守家倒计时。</p></div><div class="expedition-stats"><span><small>行者生命</small><strong>${Math.ceil(w.player.health)} / ${w.player.maxHealth}</strong></span><span><small>同行伙伴</small><strong>${w.companions.length} / ${w.companionLimit}</strong></span><span><small>旅途口粮</small><strong>${b.food}</strong></span></div>${result}`;
      if (w.run) {
        html += `<article class="journey-active"><p class="eyebrow">${w.run.phase === "cleared" ? "RETURN WITH A STORY" : "EXPEDITION IN PROGRESS"}</p><h3>${escapeHtml(w.mapDefinition?.name ?? "雾海远征")}</h3><p>${escapeHtml(w.objectiveText)}</p><div class="objective-list">${w.run.objectives.map((o, i) => `<div><span>${o.complete ? "✓" : `0${i + 1}`}</span><strong>${escapeHtml(o.name)}</strong>${button(`objective-${o.id}`, o.complete ? "已完成" : "前往", o.complete)}</div>`).join("")}</div><p>${w.boss?.boss ? escapeHtml(w.boss.boss.weakness) : escapeHtml(w.mapDefinition?.bossHint ?? "探索时记得补给，保留闪避应对守护者。")}</p><div class="shop-grid">${button("objective-exit", "前往归航门<small>自动走近并带回战利品</small>", false, true)}${button("extract", "就地归航<small>需要靠近归航门</small>", distance(w.controlled.pos, w.run.exit) > REACH + 4)}${button("retreat", "紧急撤回<small>保留采集，不领取首领奖金</small>")}</div></article>`;
      } else {
        html += `<div class="destination-grid">${Object.values(EXPEDITIONS)
          .map(
            (map, i) =>
              `<article class="destination-card destination-${map.id}" style="--biome:${map.color}"><div class="destination-art" aria-hidden="true"><span class="destination-orbit"></span><strong>${i === 0 ? "叶" : "晶"}</strong><span class="destination-number">0${i + 1}</span></div><div class="destination-copy"><p class="eyebrow">${escapeHtml(map.subtitle)}</p><h3>${escapeHtml(map.name)}</h3><p>${escapeHtml(map.description)}</p><div class="destination-boss"><small>遗迹守护者</small><strong>${escapeHtml(map.bossName)}</strong><p>${escapeHtml(map.bossHint)}</p></div>${button(`begin-expedition-${map.id}`, w.progress.awakened < 1 ? "先唤醒第一位伙伴" : `启程 · ${map.name}`, w.progress.awakened < 1, true)}</div></article>`,
          )
          .join(
            "",
          )}</div><div class="preparation-note"><strong>行前手记</strong><p>备好食物，在伙伴面板绑定续生印。灵植死亡后不会自动复活。星芒连射适合远攻，月刃适合近身清场；Z / 姿态按钮随时切换。归航门可结算远征，紧急撤回保留采集、放弃首领奖金。</p></div>`;
      }
    }
    patchHtml(el("activity-content"), html);
  }
  private flushSignals(): void {
    for (const signal of this.world.signals.splice(0)) {
      if (signal.sound) this.audio.play(signal.sound);
      if (!signal.text) continue;
      if (signal.pos) {
        this.texts.push({
          pos: { x: signal.pos.x, y: signal.pos.y - 55 },
          text: signal.text,
          color: signal.color,
          life: 1.5,
        });
        for (let i = 0; i < 6; i++)
          this.particles.push({
            pos: { ...signal.pos },
            velocity: {
              x: (Math.random() - 0.5) * 110,
              y: -Math.random() * 80,
            },
            life: 0.65,
            maxLife: 0.65,
            size: 3,
            color: signal.color,
            gravity: 60,
            shape: "leaf",
          });
        if (
          !signal.text.startsWith("-") &&
          !signal.text.startsWith("+") &&
          signal.text.length > 10
        )
          this.toast(signal.text);
      } else this.toast(signal.text);
    }
    this.particles = this.particles.slice(-160);
    this.texts = this.texts.slice(-25);
  }
  private toast(text: string): void {
    const stack = el("toast-stack");
    while (stack.children.length >= 2) stack.firstElementChild?.remove();
    const node = document.createElement("div");
    node.className = "toast";
    node.textContent = text;
    stack.append(node);
    setTimeout(() => node.remove(), 3600);
  }
  private save(manual: boolean): void {
    if (this.sample) {
      if (manual) this.toast("质量样板不写入庭园存档");
      return;
    }
    if (!this.running) return;
    try {
      localStorage.setItem(activeSaveKey(), serialize(this.world));
      window.dispatchEvent(new Event('fribench:local-save-changed'));
      if (manual) this.toast("庭园已保存");
    } catch {
      if (manual) this.toast("当前浏览器无法保存，进度仅保留到关闭页面");
    }
  }
}
